import { Button, Column, FlexBox, Icon, Text } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import type { Dashboard, QuickView } from 'lib-analytics-types';
import { CSSProperties, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { analyticsApi } from 'apis/analytics';
import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { articles } from 'utils/elevio';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useDelayedState } from 'utils/useDelayedState';

import { AnalyticsDocumentationButton } from '../documentation/analyticsDocumentationButton';

import styles from './analyticsSidenav.module.scss';
import {
  AnalyticsSidenavItemLink,
  AnalyticsSidenavSection as AnalyticsSidenavSectionT,
  AnalyticsSidenavSubItem,
} from './analyticsSidenav.types';
import { AnalyticsSidenavItem } from './components/item/analyticsSidenavItem';
import { AnalyticsSidenavSection } from './components/section/analyticsSidenavSection';

type AnalyticsSidenavProps = {
  isExpandable?: boolean;
};

export const AnalyticsSidenav = ({
  isExpandable = true,
}: AnalyticsSidenavProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [expanded, setExpanded] = useDelayedState(false, 100);
  const { pathname } = useLocation();
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const flags = useFeatureFlag(['analytics-advanced-bi']);

  const dashboards = analyticsApi.endpoints.readDashboards.useQueryState(
    undefined,
    {
      selectFromResult: ({ data }) => data?.data.dashboards ?? [],
    },
  );

  const { quickViews, isLoading } = analyticsApi.useReadFavoriteQuickViewsQuery(
    undefined,
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        quickViews: data?.data.quick_views ?? [],
      }),
    },
  );

  const isActivePath = useCallback(
    (dashboard: Dashboard, quickView?: QuickView): boolean => {
      if (quickView) {
        return (
          pathname ===
          `/analytics/dashboard/${dashboard.slug}/quick-view/${quickView.id}`
        );
      }

      return pathname === `/analytics/dashboard/${dashboard.slug}`;
    },
    [pathname],
  );

  const groupedDashboards = useMemo(
    () =>
      dashboards.reduce((result, dashboard) => {
        if (dashboard.group) {
          result.set(dashboard.group, [
            ...(result.get(dashboard.group) ?? []),
            dashboard,
          ]);
        } else {
          result.set(dashboard.title, [dashboard]);
        }

        return result;
      }, new Map<string, readonly Dashboard[]>()),
    [dashboards],
  );

  const dashboardsSection = useMemo((): AnalyticsSidenavSectionT => {
    const getLinkOptions = (
      dashboard: Dashboard,
    ): AnalyticsSidenavItemLink['options'] => ({
      path: '/analytics/dashboard/:slug',
      params: {
        path: {
          slug: dashboard.slug,
        },
      },
    });

    return {
      title: 'Dashboards',
      items: Array.from(groupedDashboards).map(([group, dashboards]) => {
        if (dashboards.length > 1) {
          return {
            type: 'accordion',
            title: group,
            icon: dashboards[0].icon,
            active: false,
            options: {
              subItems: dashboards.map((dashboard) => ({
                title: dashboard.slug === 'channels' ? 'All' : dashboard.title,
                active: isActivePath(dashboard),
                options: getLinkOptions(dashboard),
              })),
            },
          };
        }

        return {
          type: 'link',
          title: group,
          icon: dashboards[0].icon,
          active: isActivePath(dashboards[0]),
          options: getLinkOptions(dashboards[0]),
        };
      }),
    };
  }, [groupedDashboards, isActivePath]);

  const quickViewsSection = useMemo((): AnalyticsSidenavSectionT => {
    const getLinkOptions = (
      quickView: QuickView,
    ): AnalyticsSidenavItemLink['options'] => ({
      path: '/analytics/dashboard/:slug/quick-view/:id',
      params: {
        path: {
          slug: dashboards.find(
            (dashboard) => dashboard.id === quickView.dashboard_id,
          )?.slug,
          id: quickView.id,
        },
      },
    });

    return {
      title: 'Quick views',
      items: [
        {
          type: 'accordion',
          title: 'Quick views',
          icon: 'star',
          active: false,
          options: {
            isLoading,
            emptyState: (
              <FlexBox flexDirection="column" alignItems="center" gap="300">
                <FlexBox flexDirection="column" alignItems="center" gap="100">
                  <Text.Paragraph variant="3" subdued>
                    No favorites yet
                  </Text.Paragraph>

                  <Text.Paragraph variant="4" subdued>
                    You haven&apos;t favorited any quick views.
                  </Text.Paragraph>
                </FlexBox>

                <AppLink path="/analytics/quick-views">
                  <Button variant="secondary">See all quick views</Button>
                </AppLink>
              </FlexBox>
            ),
            subItems: quickViews.length
              ? [
                  {
                    title: 'All',
                    active: pathname === '/analytics/quick-views',
                    options: {
                      path: '/analytics/quick-views',
                    },
                  },
                  ...quickViews.map((quickView) => {
                    const dashboard = dashboards.find(
                      ({ id }) => id === quickView.dashboard_id,
                    );

                    return {
                      title: quickView.name,
                      active: dashboard
                        ? isActivePath(dashboard, quickView)
                        : false,
                      options: getLinkOptions(quickView),
                    } as AnalyticsSidenavSubItem;
                  }),
                ]
              : [],
            action: (
              <AnalyticsDocumentationButton
                type="article"
                articleId={articles.analytics.quickViews}
              />
            ),
          },
        },
      ],
    };
  }, [dashboards, isActivePath, isLoading, pathname, quickViews]);

  const advancedAnalyticsSection =
    useMemo((): AnalyticsSidenavSectionT | null => {
      if (!flags.analyticsAdvancedBi) return null;

      return {
        title: 'Business intelligence',
        items: [
          {
            type: 'accordion',
            title: 'Business intelligence',
            icon: 'miuros',
            active: false,
            options: {
              subItems: [
                {
                  title: 'Conversation intelligence',
                  active: pathname.startsWith(
                    '/analytics/advanced/bi/conversations',
                  ),
                  options: {
                    path: '/analytics/advanced/bi/conversations',
                  },
                },
                {
                  title: 'Event intelligence',
                  active: pathname.startsWith('/analytics/advanced/bi/events'),
                  options: {
                    path: '/analytics/advanced/bi/events',
                  },
                },
              ],
            },
          },
        ],
      };
    }, [flags.analyticsAdvancedBi, pathname]);

  const pinnedBottomSection: AnalyticsSidenavSectionT = {
    title: 'More',
    items: [
      {
        type: 'button',
        title: 'Send feedback',
        icon: 'paper-plane',
        active: false,
        options: {
          onClick: () =>
            dispatch(
              appActions.showModal({
                modal: {
                  type: 'app:feedbackForm',
                  arg: {
                    title: 'Analytics Feedback',
                    team: 'insights',
                  },
                },
              }),
            ),
        },
      },
    ],
  };

  const sections: readonly AnalyticsSidenavSectionT[] = [
    dashboardsSection,
    quickViewsSection,
    ...(advancedAnalyticsSection ? [advancedAnalyticsSection] : []),
  ];

  return (
    <nav
      aria-label="Analytics Sidenav"
      className={classNames(styles.sidenav, {
        [styles.sidenavExpanded]: expanded,
      })}
      style={
        {
          '--sidenav-width-collapsed': analyticsSidenavWidth,
        } as CSSProperties
      }
      {...(isExpandable
        ? {
            onMouseEnter: () => setExpanded(true),
            onMouseLeave: () => setExpanded(false),
          }
        : {})}
    >
      <Column className={styles.sections}>
        <Column.Row
          className={classNames(styles.expander, {
            [styles.expanderVisible]: !expanded,
          })}
          noHorizontalPadding
          noVerticalPadding
          noMinHeight
        >
          {isExpandable && (
            <Icon name="angle-double-right" size={24} aria-label="Expand" />
          )}
        </Column.Row>

        <Column.Row
          scrollable
          noHorizontalPadding
          noVerticalPadding
          noMinHeight
          className={styles.scrollable}
        >
          {sections.map((section, sectionIndex) => {
            const sectionKey = `section-${sectionIndex}`;

            return (
              <AnalyticsSidenavSection
                key={sectionKey}
                section={section}
                hidden={!expanded}
              >
                {section.items.map((item, itemIndex) => (
                  <AnalyticsSidenavItem
                    key={`${sectionKey}-item-${itemIndex}`}
                    item={item}
                  />
                ))}
              </AnalyticsSidenavSection>
            );
          })}
        </Column.Row>

        <Column.Row
          borderTop={expanded}
          noHorizontalPadding
          noVerticalPadding
          noMinHeight
        >
          <AnalyticsSidenavSection
            section={pinnedBottomSection}
            hidden={isSmallScreen ?? false}
          >
            {pinnedBottomSection.items.map((item) => (
              <AnalyticsSidenavItem key={item.title} item={item} />
            ))}
          </AnalyticsSidenavSection>
        </Column.Row>
      </Column>
    </nav>
  );
};

export const analyticsSidenavWidth = '56px';
